import {
  Box,
  Button,
  IconButton,
  InputLabel,
  TextField,
  Typography,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { getIsValidURLFormat } from '@prismamedia/one-components';
import React from 'react';
import { actions, ArticleEditAction } from '../../reducer';
import { GetArticleQuery_query_article_Article_bibliographies_ArticleBibliography } from '../../../../__generated__/queries-web';

const REQUIRED_FIELD_MSG = 'Ce champ est requis';
const INVALID_URL_MSG = "Le format de l'URL est invalide";

function getBibliographyFieldErrors(bib: {
  title?: string | null;
  url?: string | null;
}) {
  const isTitleFilled = !!bib.title?.trim();
  const trimmedUrl = bib.url?.trim() ?? '';
  const isUrlFilled = trimmedUrl.length > 0;
  const hasUrlFormatError = isUrlFilled && !getIsValidURLFormat(trimmedUrl);
  const hasTitleError = isUrlFilled && !isTitleFilled;
  const hasUrlError = hasUrlFormatError;
  return {
    hasTitleError,
    hasUrlError,
    titleHelper: hasTitleError
      ? REQUIRED_FIELD_MSG
      : hasUrlFormatError
      ? '\u00A0'
      : '',
    urlHelperText: hasUrlFormatError ? INVALID_URL_MSG : '',
    helperMinHeight: hasTitleError || hasUrlError ? '1.5rem' : 0,
  };
}

interface ExternalSourcesInputProps {
  bibliographies: GetArticleQuery_query_article_Article_bibliographies_ArticleBibliography[];
  dispatch: React.Dispatch<ArticleEditAction>;
}

export const ExternalSourcesInput: React.FC<ExternalSourcesInputProps> = ({
  bibliographies,
  dispatch,
}) => {
  const handleAdd = () => {
    const newBibliographies = [
      ...bibliographies,
      {
        id: `temp-${Date.now()}-${Math.random()}`,
        title: '',
        url: '',
      } as GetArticleQuery_query_article_Article_bibliographies_ArticleBibliography,
    ];
    dispatch(
      actions.updateField({
        field: 'bibliographies',
        value: newBibliographies,
      }),
    );
  };

  const handleDelete = (index: number) => {
    const newBibliographies = bibliographies.filter((_, i) => i !== index);
    dispatch(
      actions.updateField({
        field: 'bibliographies',
        value: newBibliographies,
      }),
    );
  };

  const handleFieldChange = (
    index: number,
    field: 'title' | 'url',
    value: string,
  ) => {
    const newBibliographies = bibliographies.map((bib, i) =>
      i === index ? { ...bib, [field]: value } : bib,
    );
    dispatch(
      actions.updateField({
        field: 'bibliographies',
        value: newBibliographies,
      }),
    );
  };

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <InputLabel shrink>Sources Externes</InputLabel>
        <Button
          variant="outlined"
          size="small"
          onClick={handleAdd}
          sx={{ textTransform: 'none' }}
        >
          + Ajouter
        </Button>
      </Box>
      {bibliographies.length === 0 ? (
        <Typography
          variant="body2"
          sx={{ color: 'text.disabled', opacity: 0.7 }}
        >
          Aucune source externe ajoutée
        </Typography>
      ) : (
        bibliographies.map((bibliography, index) => {
          const fieldErrors = getBibliographyFieldErrors(bibliography);
          return (
            <Box
              key={bibliography.id}
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'flex-start',
                mb: 2,
              }}
            >
              <TextField
                fullWidth
                placeholder="Titre du site / page"
                value={bibliography.title}
                onChange={(e) =>
                  handleFieldChange(index, 'title', e.target.value)
                }
                variant="standard"
                error={fieldErrors.hasTitleError}
                helperText={fieldErrors.titleHelper}
                FormHelperTextProps={{
                  sx: { minHeight: fieldErrors.helperMinHeight, margin: 0 },
                }}
                sx={{ flex: 1 }}
              />
              <TextField
                fullWidth
                placeholder="https://..."
                value={bibliography.url}
                onChange={(e) =>
                  handleFieldChange(index, 'url', e.target.value)
                }
                variant="standard"
                error={fieldErrors.hasUrlError}
                helperText={fieldErrors.urlHelperText}
                FormHelperTextProps={{
                  sx: { minHeight: fieldErrors.helperMinHeight, margin: 0 },
                }}
                sx={{ flex: 1 }}
              />
              <IconButton
                onClick={() => handleDelete(index)}
                size="small"
                sx={{
                  color: 'error.main',
                  '&:hover': {
                    backgroundColor: 'error.light',
                    color: 'error.dark',
                  },
                }}
              >
                <Delete />
              </IconButton>
            </Box>
          );
        })
      )}
    </Box>
  );
};
